import { useCallback, useEffect, useState } from "react";
import qapi from "views_shared/app/javascript/api";
import { throwOnApiError } from "views_shared/app/javascript/api/api";

// Reads first/last name off the promoter profile that self-represents this expert. The experts view
// carries only the composed `name`, but the rename endpoint takes the two halves, so the form has to
// prefill from the profile record itself.
//
// Called with a null profileId whenever the form has no name inputs — an expert with no
// self-representing profile, or a viewer who may edit the entity but not the person. That is a hard
// requirement, not an optimization: the profiles view applies RLS, so a hidden profile simply is not
// readable by a viewer who does not own it, and firing the read anyway would surface an error on a
// form that never needed the data.
//
// getResource RESOLVES with `{ error }` on a non-2xx (only transport failures reject), so a failed read
// would otherwise look like "profile has no name". throwOnApiError turns it into a real failure, which
// the form shows instead of prefilling the inputs with blanks a save would then persist.
const useExpertProfileNames = (profileId) => {
  const [names, setNames] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(profileId));
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!profileId) {
      setNames(null);
      setError(null);
      setIsLoading(false);
      return () => {};
    }

    setIsLoading(true);

    (async () => {
      try {
        const response = await qapi.userProfiles.get(profileId);
        throwOnApiError(response);
        const rows = Array.isArray(response.data) ? response.data : [];
        // RLS filters unreadable rows instead of erroring, so success-with-zero-rows for a known id is
        // still a failed read — not a person with no name.
        if (!rows.length) throw new Error(`user profile ${profileId} is not readable`);
        if (cancelled) return;
        setNames({ first_name: rows[0].first_name, last_name: rows[0].last_name });
        setError(null);
      } catch (readError) {
        if (cancelled) return;
        setNames(null);
        setError(readError);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [profileId]);

  // After a partial save (rename committed, entity PATCH failed) the form's diff baseline must advance
  // past the committed halves, or a retry of the remaining fields would resend the rename.
  const applyCommittedNames = useCallback((committed) => {
    setNames((previous) => ({ ...previous, ...committed }));
  }, []);

  return { names, isLoading, error, applyCommittedNames };
};

export default useExpertProfileNames;
